/**
 * Public web-origin policy for indexable browser metadata.
 *
 * This is intentionally separate from platform/config.ts: runtime branding may
 * roll back independently, while canonical URLs stay on the global DRIVESHARE
 * route. Deployment previews can override the origin explicitly.
 */
import { resolvedLegacyCanonical, resolvedCanonicalJsonLd } from "../../../../lib/copy/src/public-canonical-client.mjs";
import { isDriveshareLegacySeoEnabled } from "../../../../lib/copy/src/legacy-seo-switch.mjs";
export { canonicalJsonLd };
function canonicalJsonLd<T>(value:T,oldUrls:string[],canonical:string):T {
  return resolvedCanonicalJsonLd(value,oldUrls,canonical,isDriveshareLegacySeoEnabled());
}
export function legacyCanonicalUrl(path: string, fallback: string, hints: { currentCar?: Record<string, unknown> } = {}): string {
  return resolvedLegacyCanonical(path, fallback, { ...hints, legacySeoEnabled:isDriveshareLegacySeoEnabled() });
}
export const DEFAULT_CANONICAL_PUBLIC_ORIGIN = "https://driveshare.com";
const DEFAULT_LEGACY_PUBLIC_ORIGIN = "https://www.bookaclassic.com";

function normalizeOrigin(raw: string | undefined, fallback: string): string {
  const value = (raw || fallback).trim() || fallback;
  const withScheme = /^https?:\/\//i.test(value) ? value : `https://${value}`;
  try {
    const url = new URL(withScheme);
    return `${url.protocol}//${url.host}`;
  } catch {
    return fallback;
  }
}

function sameHostname(left: string, right: string): boolean {
  const normalize = (value: string) => value.toLowerCase().replace(/^www\./, "");
  return normalize(left) === normalize(right);
}

function isDriveShareOrigin(origin: string): boolean {
  try {
    return sameHostname(
      new URL(origin).hostname,
      new URL(getUsPublicOrigin()).hostname,
    );
  } catch {
    return false;
  }
}

function getLegacyPublicOrigin(): string {
  return normalizeOrigin(
    import.meta.env.VITE_CANONICAL_LEGACY_ORIGIN ||
      import.meta.env.VITE_PUBLIC_DOMAIN_BAC,
    DEFAULT_LEGACY_PUBLIC_ORIGIN,
  );
}

export function getCanonicalPublicOrigin(): string {
  const enabled = /^true$/i.test(
    String(import.meta.env.VITE_GLOBAL_DRIVESHARE_CANONICAL_ENABLED || "").trim(),
  );
  if (!enabled) {
    return normalizeOrigin(
      import.meta.env.VITE_CANONICAL_LEGACY_ORIGIN ||
        import.meta.env.VITE_PUBLIC_DOMAIN_BAC ||
        DEFAULT_LEGACY_PUBLIC_ORIGIN,
      DEFAULT_LEGACY_PUBLIC_ORIGIN,
    );
  }
  const explicit =
    import.meta.env.VITE_CANONICAL_PUBLIC_ORIGIN ||
    import.meta.env.VITE_PUBLIC_DOMAIN_DRIVESHARE;
  return normalizeOrigin(explicit, DEFAULT_CANONICAL_PUBLIC_ORIGIN);
}

/** DriveShare-owned URLs always use this origin, independent of rollout flags. */
export function getUsPublicOrigin(): string {
  return normalizeOrigin(
    import.meta.env.VITE_PUBLIC_DOMAIN_DRIVESHARE,
    DEFAULT_CANONICAL_PUBLIC_ORIGIN,
  );
}

/**
 * Resolve the origin for canonical links rendered by the browser.
 *
 * Keep the global rollout flag authoritative on BookAClassic and other hosts.
 * A page actually served on DriveShare must, however, canonicalize to the
 * DriveShare-owned origin even while that separate global rollout is staged.
 */
export function getRenderedCanonicalPublicOrigin(browserOrigin: string): string {
  if (isDriveShareOrigin(browserOrigin)) return getUsPublicOrigin();
  return getCanonicalPublicOrigin();
}

/** Build a canonical href while correcting stale global-origin absolutes. */
export function getRenderedCanonicalUrl(canonical: string, browserOrigin: string): string {
  const renderedOrigin = getRenderedCanonicalPublicOrigin(browserOrigin);
  try {
    const url = new URL(canonical, renderedOrigin);
    const globalOrigin = getCanonicalPublicOrigin();
    const canonicalIsBrandOrigin =
      url.origin === globalOrigin ||
      url.origin === getLegacyPublicOrigin() ||
      isDriveShareOrigin(url.origin);
    if (isDriveShareOrigin(browserOrigin) && canonicalIsBrandOrigin) {
      return `${renderedOrigin}${url.pathname}${url.search}${url.hash}`;
    }
    return url.toString();
  } catch {
    return canonical;
  }
}

export function buildPreservedCarShareUrl(
  canonicalPath: string,
  browserOrigin: string,
): string {
  const origin = canonicalPath.startsWith("/car/")
    ? getUsPublicOrigin()
    : normalizeOrigin(browserOrigin, getCanonicalPublicOrigin());
  return `${origin}${canonicalPath}`;
}

export function canonicalPublicUrl(path = "/"): string {
  const trailing = path.startsWith("/") ? path : `/${path}`;
  return `${getCanonicalPublicOrigin()}${trailing === "/" ? "" : trailing}`;
}